import React from "react";
import PropTypes from "prop-types";
import { useFormContext } from "react-hook-form";

// Bulk User Management download-column config. Sibling of the trip CSV Columns
// section: same pill-button UI, bound to the SAME react-hook-form so the tab's
// single bottom "Save" persists both. Admin-only: the companies/edit page is
// reachable only by admin and client_manager (CompanyPolicy#edit?), so this
// section is gated on the bulk_user_management flag AND current_user.admin? —
// both surfaced on the company payload by CompaniesController#edit.
export const USER_CSV_COLUMN_LABELS = {
    name: "Name",
    surname: "Surname",
    email: "Email",
    employee_number: "Employee number",
    contact_number: "Contact number",
    address_primary: "Address primary",
    address_distance: "Address distance",
    status: "Status",
};

export const USER_CSV_COLUMN_KEYS = Object.keys(USER_CSV_COLUMN_LABELS);

const UserCsvColumns = ({ selectedCompany, isLoading }) => {
    const { watch, setValue } = useFormContext();
    const columns = watch("user_csv_column") || {};

    const isVisible =
        selectedCompany.bulk_user_management_enabled &&
        selectedCompany.can_edit_user_csv_columns;

    if (!isVisible) return null;

    const applyColumns = (next) =>
        setValue("user_csv_column", { ...columns, ...next }, { shouldDirty: true });
    const toggle = (key) => applyColumns({ [key]: !columns[key] });
    const setAll = (value) =>
        applyColumns(
            USER_CSV_COLUMN_KEYS.reduce((acc, key) => ({ ...acc, [key]: value }), {})
        );

    return (
        <>
            <hr className="border border-gray-200 my-3" />

            <div
                className="user_csv_column flex flex-wrap px-3 md:px-0"
                data-testid="user-csv-columns-section"
            >
                <div
                    className="w-full md:w-1/4 flex flex-col pr-5 mb-4 md:mb-0"
                    style={{ whiteSpace: "pre-wrap" }}
                >
                    <div className="w-full pr-5">
                        <h3 className="text-lg font-medium text-gray-700">User CSV Columns</h3>
                        <p className="text-sm text-gray-500">
                            Columns included in bulk user CSV downloads
                        </p>
                    </div>
                </div>

                <div className="w-full md:w-3/4 flex flex-col">
                    <div className="w-full flex flex-wrap">
                        <button
                            onClick={() => setAll(true)}
                            className={`p-2 mt-2 mb-2 mr-2 text-sm flex align-center justify-center relative cursor-pointer underline ${
                                isLoading ? "cursor-wait" : ""
                            } bg-transparent text-blue-500 hover:text-blue-700`}
                        >
                            Select All
                        </button>
                        <button
                            onClick={() => setAll(false)}
                            className={`p-2 mt-2 mb-2 mr-2 text-sm flex align-center justify-center relative cursor-pointer underline ${
                                isLoading ? "cursor-wait" : ""
                            } bg-transparent text-blue-500 hover:text-blue-700`}
                        >
                            Deselect All
                        </button>
                    </div>

                    <div className="w-full flex flex-wrap">
                        {USER_CSV_COLUMN_KEYS.map((key) => (
                            <button
                                key={key}
                                onClick={() => toggle(key)}
                                data-testid={`user-csv-column-${key}`}
                                className={`p-3 pr-4 pl-4 mt-2 mb-2 mr-2 text-sm flex items-center justify-center relative cursor-pointer border rounded ${
                                    isLoading ? "cursor-wait" : ""
                                } ${
                                    columns[key]
                                        ? "button"
                                        : "bg-transparent text-black border-black"
                                } ${
                                    !columns[key] ? "hover:bg-gray-200 hover:border-black" : ""
                                }`}
                                disabled={isLoading}
                            >
                                {USER_CSV_COLUMN_LABELS[key]}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </>
    );
};

UserCsvColumns.propTypes = {
    selectedCompany: PropTypes.shape({
        bulk_user_management_enabled: PropTypes.bool,
        can_edit_user_csv_columns: PropTypes.bool,
    }).isRequired,
    isLoading: PropTypes.bool,
};

export default UserCsvColumns;
